import React from 'react';
import { motion } from 'framer-motion';
import { Box, Typography, Container, alpha, Grid } from '@mui/material';
import {
  GpsFixed as KPIIcon,
  Psychology as AIIcon,
  Chat as ChatIcon,
  Dashboard as DashboardIcon,
  Hub as IntegrationsIcon,
  HeadsetMic as ConsultingIcon,
  AutoAwesome as SparkleIcon,
} from '@mui/icons-material';
import { tokens } from '../../theme';

const FeaturesSection: React.FC = () => {
  const features = [
    {
      icon: <KPIIcon />,
      title: 'Smart KPIs',
      description: 'We guess your industry from your website and pick a starting set of metrics. It is keyword matching, not magic, and you can change what it picked.',
      gradient: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
      color: '#6366F1',
    },
    {
      icon: <ConsultingIcon />,
      title: 'Human Consulting',
      description: 'Strategy sessions and hands-on help from a person, on the Guided tier and above. The Self-Serve tier gets the same email address without priority, so check the plan before you count on it.',
      gradient: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
      color: '#8B5CF6',
    },
    {
      icon: <AIIcon />,
      title: 'AI Insights',
      description: 'Anomaly detection over your sales history, flagging the days that do not fit your pattern. It tells you after the fact, not before: it is reading what already happened.',
      gradient: 'linear-gradient(135deg, #EC4899 0%, #F43F5E 100%)',
      color: '#EC4899',
    },
    {
      icon: <ChatIcon />,
      title: 'Chat with Data',
      description: 'Ask questions in plain English. "What were my takings last week?" It answers from your synced metrics. It will not tell you why something happened, because that is not built.',
      gradient: 'linear-gradient(135deg, #10B981 0%, #06D6A0 100%)',
      color: '#10B981',
    },
    {
      icon: <DashboardIcon />,
      title: 'Beautiful Dashboards',
      description: 'Data that\'s actually readable. Clean, modern visualizations that tell a story at a glance.',
      gradient: 'linear-gradient(135deg, #3B82F6 0%, #6366F1 100%)',
      color: '#3B82F6',
    },
    {
      icon: <IntegrationsIcon />,
      title: 'Clover and Stripe, Connected',
      description: 'Connect Clover and Stripe over OAuth. More platforms are being built, and we will say which rather than imply all of them.',
      gradient: 'linear-gradient(135deg, #F59E0B 0%, #FB923C 100%)',
      color: '#F59E0B',
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        background: tokens.background.paper,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Grid Pattern */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            radial-gradient(${alpha(tokens.primary.main, 0.1)} 1px, transparent 1px)
          `,
          backgroundSize: '30px 30px',
          opacity: 0.4,
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative' }}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0.15, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                px: 2,
                py: 0.75,
                borderRadius: '100px',
                background: alpha(tokens.primary.main, 0.08),
                border: `1px solid ${alpha(tokens.primary.main, 0.15)}`,
              }}
            >
              <SparkleIcon sx={{ fontSize: 16, color: tokens.primary.main }} />
              <Typography
                variant="caption"
                sx={{ color: tokens.primary.main, fontWeight: 600 }}
              >
                Powerful Features
              </Typography>
            </Box>

            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                color: tokens.text.primary,
                mb: 2,
                fontSize: { xs: '2rem', md: '2.75rem' },
                letterSpacing: '-0.03em',
              }}
            >
              Everything you need to{' '}
              <Box
                component="span"
                sx={{
                  background: tokens.primary.gradient,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                understand your business
              </Box>
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: tokens.text.secondary,
                maxWidth: 600,
                mx: 'auto',
                fontSize: '1.1rem',
                lineHeight: 1.7,
              }}
            >
              Powerful analytics made simple. No data science degree required.
            </Typography>
          </Box>
        </motion.div>

        {/* Features Grid */}
        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} lg={4} key={index}>
              <motion.div
                initial={{ opacity: 0.15, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Box
                  sx={{
                    height: '100%',
                    p: 4,
                    borderRadius: '20px',
                    background: tokens.background.subtle,
                    border: `1px solid ${tokens.border.light}`,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'default',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      borderColor: alpha(feature.color, 0.3),
                      boxShadow: `0 20px 40px ${alpha(feature.color, 0.15)}`,
                      '& .feature-icon': {
                        transform: 'scale(1.1) rotate(5deg)',
                      },
                      '& .feature-glow': {
                        opacity: 1,
                      },
                    },
                  }}
                >
                  {/* Glow Effect */}
                  <Box
                    className="feature-glow"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: `radial-gradient(circle at top left, ${alpha(feature.color, 0.08)} 0%, transparent 60%)`,
                      opacity: 0,
                      transition: 'opacity 0.4s ease',
                      pointerEvents: 'none',
                    }}
                  />

                  <Box
                    className="feature-icon"
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '16px',
                      background: feature.gradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      mb: 3,
                      boxShadow: `0 12px 24px ${alpha(feature.color, 0.3)}`,
                      transition: 'transform 0.4s ease',
                    }}
                  >
                    {React.cloneElement(feature.icon, { sx: { fontSize: 30 } })}
                  </Box>

                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: tokens.text.primary,
                      mb: 1.5,
                    }}
                  >
                    {feature.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: tokens.text.secondary,
                      lineHeight: 1.7,
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default FeaturesSection;
